import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';

/**
 * Persister para React Query basado en `localStorage`. localStorage es
 * síncrono pero `createAsyncStoragePersister` acepta una API async — lo
 * envolvemos con un `Promise.resolve` mínimo.
 *
 * Por qué localStorage y no IndexedDB:
 *   - El outbox ya usa IndexedDB (Dexie) y es nuestra fuente de verdad
 *     para mutaciones. La cache de React Query es estado derivado: si
 *     se pierde, se refetch.
 *   - localStorage soporta 5-10 MB → ~10× lo que una sesión típica de
 *     React Query ocupa.
 *   - Síncrono = arranque más rápido. Tras un reload offline, la UI
 *     dibuja inmediatamente desde cache sin esperar a IndexedDB.
 *
 * Solo se inicializa cuando se llama desde un contexto cliente — los
 * call-sites están dentro de `'use client'`.
 */

const STORAGE_KEY = 'boosters-ai-query-cache';

// localStorage está disponible solo en cliente. En SSR devolvemos un
// stub que no persiste nada — los GC del persister manejan ausencia de
// storage sin romperse.
function getStorage() {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return {
      getItem: async () => null,
      setItem: async () => undefined,
      removeItem: async () => undefined,
    };
  }
  return {
    getItem: async (key: string): Promise<string | null> => {
      try {
        return window.localStorage.getItem(key);
      } catch {
        // QuotaExceededError o storage deshabilitado en privacy mode.
        return null;
      }
    },
    setItem: async (key: string, value: string): Promise<void> => {
      try {
        window.localStorage.setItem(key, value);
      } catch (e) {
        const err = e as { name?: string };
        const isQuota = err?.name === 'QuotaExceededError';
        if (isQuota) {
          // Quota llena: intentamos recuperar borrando el cache de
          // React Query (somos su mayor consumidor, normalmente). Si
          // libera espacio suficiente, reintentamos el write. Si NO
          // libera, emitimos un evento para que la UI pueda avisar y
          // se da por perdido el persist hasta que el user actúe.
          try {
            window.localStorage.removeItem(key);
            window.localStorage.setItem(key, value);
            return;
          } catch {
            // Reintento también falló: ya no podemos persistir cache.
          }
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('outbox:storage-full'));
          }
           
          console.warn(
            '[query-persister] QuotaExceededError: localStorage lleno, la cache de React Query no persistirá hasta que se libere espacio.',
          );
        }
        // Otros errores (storage deshabilitado en private mode, etc.)
        // se ignoran silenciosamente — la cache se reconstruirá del
        // servidor en el próximo arranque online.
      }
    },
    removeItem: async (key: string): Promise<void> => {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}

export function createQueryPersister() {
  return createAsyncStoragePersister({
    storage: getStorage(),
    key: STORAGE_KEY,
    throttleTime: 1000,
  });
}
